const allowedReturnPrefixes = [
  "/app",
  "/redeem",
  "/invitations/accept",
  "/support",
  "/support/approve",
];

// A support-approval or family-invitation link carries its single-use token in the fragment, and
// its page needs the token after sign-in. Only that exact fragment, on those two pages, is kept;
// every other fragment is dropped. It is stored with the sign-in under way: that can be used for
// at most 15 minutes, is cleared when sign-in finishes or is cancelled, and an abandoned one is
// removed the next time the app opens in that browser.
const tokenFragmentPaths = new Set(["/support/approve", "/invitations/accept"]);
const linkTokenFragment = /^#token=[A-Za-z0-9_-]{43}$/u;

export function safeReturnPath(value: unknown) {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) return "/app";
  try {
    const parsed = new URL(value, window.location.origin);
    if (parsed.origin !== window.location.origin) return "/app";
    const parameters = new URLSearchParams(parsed.search);
    for (const key of [...parameters.keys()]) {
      if (/(?:email|code|voucher|gift)/iu.test(key)) parameters.delete(key);
    }
    const query = parameters.toString();
    const fragment =
      tokenFragmentPaths.has(parsed.pathname) && linkTokenFragment.test(parsed.hash)
        ? parsed.hash
        : "";
    const path = `${parsed.pathname}${query ? `?${query}` : ""}${fragment}`;
    return allowedReturnPrefixes.some(
      (prefix) => parsed.pathname === prefix || parsed.pathname.startsWith(`${prefix}/`),
    )
      ? path
      : "/app";
  } catch {
    return "/app";
  }
}

let fragmentCode: string | null | undefined;

export function clearEmailCodeFragmentCache() {
  fragmentCode = undefined;
}

/**
 * Take the native Cognito email code out of the fragment and scrub the address bar before any
 * network request or render can expose it. The value stays only in this tab's memory.
 */
export function takeEmailCodeFromFragment() {
  if (fragmentCode !== undefined && !window.location.hash) return fragmentCode;
  const parameters = new URLSearchParams(window.location.hash.slice(1));
  const code = parameters.get("code")?.trim() ?? "";
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );
  fragmentCode = /^\d{6}(?:\d{2})?$/.test(code) ? code : null;
  return fragmentCode;
}

export function maskEmail(email: string) {
  const [local = "", domain = ""] = email.trim().toLowerCase().split("@");
  const domainParts = domain.split(".");
  const topLevel = domainParts.length > 1 ? domainParts.pop() : "";
  const domainName = domainParts.join(".");
  const maskPart = (value: string) => {
    if (!value) return "";
    if (value.length === 1) return `${value}•`;
    return `${value[0]}${"•".repeat(Math.min(6, value.length - 1))}`;
  };
  if (!local || !domainName || !topLevel) return "your email address";
  return `${maskPart(local)}@${maskPart(domainName)}.${topLevel}`;
}
